import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, ArrowUp, ArrowDown, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * DashboardCustomizeDialog — permite ocultar y reordenar tarjetas del Dashboard.
 *
 * Props:
 *   open, onOpenChange
 *   prefs = { hidden: [ids], order: [ids], card_ids: [ids] }
 *   labels = { [id]: "Etiqueta legible" }
 *   sections = { [sectionKey]: { label, ids: [ids] } }
 *   onSave(next), onReset()
 */
export default function DashboardCustomizeDialog({
  open,
  onOpenChange,
  prefs,
  labels,
  sections,
  onSave,
  onReset,
}) {
  const [draft, setDraft] = useState({ hidden: [], order: [] });

  useEffect(() => {
    if (open) {
      setDraft({
        hidden: Array.from(prefs.hidden || []),
        order: Array.from(prefs.order || []),
      });
    }
  }, [open, prefs]);

  const isHidden = (id) => draft.hidden.includes(id);

  const toggleHidden = (id) => {
    setDraft((d) => ({
      ...d,
      hidden: d.hidden.includes(id) ? d.hidden.filter((x) => x !== id) : [...d.hidden, id],
    }));
  };

  /** Mueve el id 1 posición arriba o abajo, solo dentro de su sección. */
  const move = (id, dir, sectionIds) => {
    setDraft((d) => {
      const inSection = new Set(sectionIds);
      const globalOrder = Array.from(d.order);
      // Índices GLOBALES de los items de la sección, en el orden actual
      const sectionIdxs = globalOrder
        .map((cid, i) => (inSection.has(cid) ? i : -1))
        .filter((i) => i >= 0);
      const currIdx = globalOrder.indexOf(id);
      const posInSection = sectionIdxs.indexOf(currIdx);
      const targetPosInSection = posInSection + dir;
      if (targetPosInSection < 0 || targetPosInSection >= sectionIdxs.length) return d;
      const swapWithGlobalIdx = sectionIdxs[targetPosInSection];
      // Swap
      [globalOrder[currIdx], globalOrder[swapWithGlobalIdx]] = [
        globalOrder[swapWithGlobalIdx],
        globalOrder[currIdx],
      ];
      return { ...d, order: globalOrder };
    });
  };

  const handleSave = async () => {
    try {
      await onSave(draft);
      toast.success("Preferencias guardadas");
      onOpenChange(false);
    } catch (e) {
      toast.error("No se pudo guardar");
    }
  };

  const handleReset = async () => {
    try {
      await onReset();
      toast.success("Restaurado a valores por defecto");
      onOpenChange(false);
    } catch (e) {
      toast.error("No se pudo restaurar");
    }
  };

  const sectionKeys = Object.keys(sections || {});
  const hiddenCount = draft.hidden.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dashboard-customize-dialog">
        <DialogHeader>
          <DialogTitle>Personalizar Dashboard</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground mb-3">
          Oculta las tarjetas que no necesitas o reordénalas dentro de su sección.
          Los cambios se guardan en tu perfil y aparecen en todos tus dispositivos.
        </p>

        {sectionKeys.map((sk) => {
          const section = sections[sk];
          const sectionIdSet = new Set(section.ids);
          // Ordenar por posición actual en `draft.order`
          const ordered = draft.order.filter((id) => sectionIdSet.has(id));
          // Añadir cualquier id no ordenado al final
          for (const id of section.ids) if (!ordered.includes(id)) ordered.push(id);

          return (
            <div key={sk} className="mb-4 border border-border rounded-md overflow-hidden" data-testid={`customize-section-${sk}`}>
              <div className="bg-muted/40 px-3 py-2 border-b border-border">
                <div className="overline text-[10px]">{section.label}</div>
              </div>
              <div className="divide-y divide-border/60">
                {ordered.map((id, idx) => (
                  <div key={id} className="flex items-center justify-between px-3 py-2 hover:bg-accent/30" data-testid={`customize-card-${id}`}>
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className={`text-sm font-medium truncate ${isHidden(id) ? "line-through text-muted-foreground" : ""}`}>
                        {labels[id] || id}
                      </span>
                      {isHidden(id) && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase">
                          Oculta
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => move(id, -1, section.ids)}
                        disabled={idx === 0}
                        title="Subir"
                        data-testid={`customize-up-${id}`}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => move(id, 1, section.ids)}
                        disabled={idx === ordered.length - 1}
                        title="Bajar"
                        data-testid={`customize-down-${id}`}
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => toggleHidden(id)}
                        title={isHidden(id) ? "Mostrar" : "Ocultar"}
                        data-testid={`customize-toggle-${id}`}
                      >
                        {isHidden(id) ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        <div className="text-xs text-muted-foreground">
          {hiddenCount === 0
            ? "Todas las tarjetas se muestran."
            : `${hiddenCount} tarjeta${hiddenCount === 1 ? "" : "s"} oculta${hiddenCount === 1 ? "" : "s"}.`}
        </div>

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={handleReset} data-testid="customize-reset">
            <RotateCcw className="w-4 h-4 mr-1.5" />
            Restaurar
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="customize-cancel">
            Cancelar
          </Button>
          <Button onClick={handleSave} data-testid="customize-save">Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
